import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Company.css';

const CompanyCreate = () => {
    const history = useHistory();
    const [name, setName] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [contactNumber1, setContactNumber1] = useState('');
    const [contactNumber2, setContactNumber2] = useState('');
    const [email, setEmail] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [website, setWebsite] = useState('');
    const [addressLine, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [pincode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [district, setDistrict] = useState('');
    const [state, setState] = useState('');
    const [country, setCountry] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [pinCodes, setPinCodes] = useState([]);

    const { isAuthenticated, companyId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchData('PinCodeApi', '/' + companyId, token)
            .then((r) => { if (r.responseCode === '1111') setPinCodes(r.response_data); })
            .catch((e) => console.error('Error fetching PIN codes:', e));
    }, [isAuthenticated, companyId, token]);

    const handlePinCodeChange = (value) => {
        if (value.length === 6) {
            const p = pinCodes.find((pc) => pc.Postcode === value);
            if (p) { setCity(p.City); setDistrict(p.District); setPostcode(p.Postcode); setCountry(p.Country); setState(p.State); }
        }
    };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setName(newName);
        const code = newName.toUpperCase().split(' ').map((w) => w.charAt(0)).join('');
        setCompanyCode(code + Math.floor(100 + Math.random() * 900));
    };

    const clearForm = () => {
        setName(''); setCompanyCode(''); setContactNumber1(''); setContactNumber2('');
        setEmail(''); setGstNumber(''); setWebsite(''); setAddressLine1(''); setAddressLine2('');
        setPostcode(''); setCity(''); setDistrict(''); setState(''); setCountry('');
    };

    const submitData = async (e) => {
        e.preventDefault();
        if (!name || !contactNumber1) { setErrorMessage('Name and Contact Number are required!'); return; }
        const payload = {
            Name: name, CompanyCode: companyCode, ContactNumber1: contactNumber1,
            ContactNumber2: contactNumber2, Email: email, GSTNumber: gstNumber,
            Website: website, Addressline1: addressLine, Addressline2: addressLine2,
            Pincode: pincode, City: city, District: district, State: state, Country: country,
            CreatedBy: 1, CreatedDate: new Date(),
        };
        postData('CompanyApi', payload, token)
            .then(() => {
                setSuccessMessage('Company created successfully!');
                setErrorMessage('');
                clearForm();
                history.push('/companyIndex');
            })
            .catch((e) => console.error('Company Create error:', e));
    };

    return (
        <div className="company-container">
            <div className="company-header-card">
                <div className="company-header-content">
                    <div className="company-title-section">
                        <span className="company-title-icon"><BusinessIcon fontSize="small" /></span>
                        <h1 className="company-title">Company Create</h1>
                    </div>
                    <div className="company-header-actions">
                        <button className="co-btn co-btn-save" onClick={submitData}>
                            <SaveIcon style={{ fontSize: 14 }} />&nbsp;Save
                        </button>
                        <Link to="/companyIndex" className="co-btn co-btn-back">
                            <ArrowBackIcon style={{ fontSize: 14 }} />&nbsp;Back
                        </Link>
                    </div>
                </div>
            </div>

            {errorMessage && <div className="company-alert company-alert-error">{errorMessage}</div>}
            {successMessage && <div className="company-alert company-alert-success">{successMessage}</div>}

            <form onSubmit={submitData}>
                <div className="company-form-card">
                    <p className="company-form-section-title">Basic Information</p>
                    <div className="company-form-row">
                        <div className="company-form-group w-3">
                            <label className="company-label">Company Name <span className="required">*</span></label>
                            <input className="company-input" value={name} onChange={handleNameChange} type="text" placeholder="Enter company name" required maxLength={50} />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">Company Code</label>
                            <input className="company-input" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} type="text" placeholder="Auto-generated" readOnly />
                        </div>
                    </div>
                    <div className="company-form-row">
                        <div className="company-form-group">
                            <label className="company-label">Contact Number 1 <span className="required">*</span></label>
                            <input className="company-input" value={contactNumber1} onChange={(e) => setContactNumber1(e.target.value)} type="text" placeholder="Enter contact number" required maxLength={15} />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">Contact Number 2</label>
                            <input className="company-input" value={contactNumber2} onChange={(e) => setContactNumber2(e.target.value)} type="text" placeholder="Enter contact number" maxLength={15} />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">Email</label>
                            <input className="company-input" value={email} onChange={(e) => setEmail(e.target.value)} type="text" placeholder="Enter email" maxLength={30} />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">GST Number</label>
                            <input className="company-input" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} type="text" placeholder="Enter GST number" maxLength={15} />
                        </div>
                    </div>
                </div>

                <div className="company-form-card">
                    <p className="company-form-section-title">Address</p>
                    <div className="company-form-row">
                        <div className="company-form-group w-2">
                            <label className="company-label">Address Line 1</label>
                            <input className="company-input" value={addressLine} onChange={(e) => setAddressLine1(e.target.value)} type="text" placeholder="Enter address line 1" maxLength={200} />
                        </div>
                        <div className="company-form-group w-2">
                            <label className="company-label">Address Line 2</label>
                            <input className="company-input" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} type="text" placeholder="Enter address line 2" maxLength={200} />
                        </div>
                    </div>
                    <div className="company-form-row">
                        <div className="company-form-group">
                            <label className="company-label">PIN Code</label>
                            <input className="company-input" value={pincode} onChange={(e) => setPostcode(e.target.value)} type="text" placeholder="Enter PIN code" list="pinCodesList" onBlur={(e) => handlePinCodeChange(e.target.value)} />
                            <datalist id="pinCodesList">
                                {pinCodes.map((p) => <option key={p.Id} value={p.Postcode} />)}
                            </datalist>
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">City</label>
                            <input className="company-input" value={city} onChange={(e) => setCity(e.target.value)} type="text" placeholder="Auto-filled" readOnly />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">District</label>
                            <input className="company-input" value={district} onChange={(e) => setDistrict(e.target.value)} type="text" placeholder="Auto-filled" readOnly />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">State</label>
                            <input className="company-input" value={state} onChange={(e) => setState(e.target.value)} type="text" placeholder="Auto-filled" readOnly />
                        </div>
                        <div className="company-form-group">
                            <label className="company-label">Country</label>
                            <input className="company-input" value={country} onChange={(e) => setCountry(e.target.value)} type="text" placeholder="Auto-filled" readOnly />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CompanyCreate;
